import type {
  Assignment,
  AuditEntry,
  BillingOverview,
  Contact,
  ContactList,
  ContactListItem,
  Member,
  MeResponse,
  OrgSettings,
  OwnAppSetup,
  Paged,
  Recipient,
  RecipientDetail,
  SyncOverview,
} from "@kontaktfix/shared";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "./api";

export const keys = {
  me: ["me"] as const,
  contacts: (params: object) => ["contacts", params] as const,
  contact: (id: string) => ["contact", id] as const,
  lists: ["lists"] as const,
  list: (id: string) => ["list", id] as const,
  assignments: (listId: string) => ["assignments", listId] as const,
  people: ["people"] as const,
  person: (id: string) => ["person", id] as const,
  sync: ["sync"] as const,
  activity: (params: object) => ["activity", params] as const,
  settings: ["settings"] as const,
  ownApp: ["own-app"] as const,
  members: ["members"] as const,
  billing: ["billing"] as const,
};

export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<MeResponse>("/api/me"),
    retry: false,
    staleTime: 60_000,
  });

export type ContactQuery = {
  q?: string;
  listId?: string;
  notInListId?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
};
export const useContacts = (params: ContactQuery) =>
  useQuery({
    queryKey: keys.contacts(params),
    queryFn: () => api.get<Paged<ContactListItem>>(`/api/contacts${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useContact = (id: string | null) =>
  useQuery({
    queryKey: keys.contact(id ?? ""),
    queryFn: () => api.get<Contact>(`/api/contacts/${id}`),
    enabled: Boolean(id),
  });

export const useLists = () =>
  useQuery({ queryKey: keys.lists, queryFn: () => api.get<ContactList[]>("/api/lists") });
export const useList = (id: string) =>
  useQuery({ queryKey: keys.list(id), queryFn: () => api.get<ContactList>(`/api/lists/${id}`) });
export const useAssignments = (listId: string) =>
  useQuery({
    queryKey: keys.assignments(listId),
    queryFn: () => api.get<Assignment[]>(`/api/lists/${listId}/assignments`),
  });

/** Delivery state changes in the background, so these refresh while visible. */
export const usePeople = () =>
  useQuery({
    queryKey: keys.people,
    queryFn: () => api.get<Recipient[]>("/api/people"),
    refetchInterval: 5000,
  });
export const usePerson = (id: string) =>
  useQuery({
    queryKey: keys.person(id),
    queryFn: () => api.get<RecipientDetail>(`/api/people/${id}`),
    refetchInterval: 5000,
  });
export const useSyncOverview = () =>
  useQuery({
    queryKey: keys.sync,
    queryFn: () => api.get<SyncOverview>("/api/sync/overview"),
    refetchInterval: (q) => (q.state.data?.running ? 2000 : 10_000),
  });

export const useActivity = (params: {
  entityType?: string;
  entityId?: string;
  page?: number;
  pageSize?: number;
}) =>
  useQuery({
    queryKey: keys.activity(params),
    queryFn: () => api.get<Paged<AuditEntry>>(`/api/activity${qs(params)}`),
    placeholderData: keepPreviousData,
  });

export const useSettings = () =>
  useQuery({
    queryKey: keys.settings,
    queryFn: () => api.get<OrgSettings>("/api/settings"),
    // Follows the clean-up while a disconnect is running.
    refetchInterval: (q) => (q.state.data?.connection.disconnecting ? 5000 : false),
  });
export const useOwnAppSetup = () =>
  useQuery({
    queryKey: keys.ownApp,
    queryFn: () => api.get<OwnAppSetup>("/api/connection/own-app"),
    retry: false,
  });
export const useBilling = (options?: { poll?: boolean }) =>
  useQuery({
    queryKey: keys.billing,
    queryFn: () => api.get<BillingOverview>("/api/billing"),
    refetchInterval: options?.poll ? 2000 : false,
  });

/**
 * False once the trial ended without a subscription: contacts are read-only and nothing is delivered.
 * The server enforces this; the app only hides what would fail.
 */
export function useCanChangeContacts(): boolean {
  const { data: me } = useMe();
  return me?.org?.billing.canUse ?? true;
}

export const useMembers = () =>
  useQuery({ queryKey: keys.members, queryFn: () => api.get<Member[]>("/api/members") });

/** After a change, refresh everything that may show it (lists, contacts, delivery state). */
export function useInvalidateData() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["contacts"] }),
      client.invalidateQueries({ queryKey: ["contact"] }),
      client.invalidateQueries({ queryKey: keys.lists }),
      client.invalidateQueries({ queryKey: ["list"] }),
      client.invalidateQueries({ queryKey: ["assignments"] }),
      client.invalidateQueries({ queryKey: keys.people }),
      client.invalidateQueries({ queryKey: keys.sync }),
      client.invalidateQueries({ queryKey: ["activity"] }),
    ]);
}
